import {
    Contract,
    Manufacture,
    Model,
    SingleProductConfigOptionProps,
} from '@/modules/Contract/types'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, Typogaphy, FormItem } from 'vnetwork-ui'
import ConfigurationIcons from '@/icons/ConfigurationIcons'
import manufactures from '../Warehouse/manufactures.json'
import models from '../Warehouse/models.json'

const ServerWarehouse: React.FC<SingleProductConfigOptionProps> = ({
    index,
    name,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()

    const nameValue = useWatch({
        control: formControl.control,
        name: `${name}.[${index}].name` as Path<Contract>,
    })

    const warehouseTypeMapingName = {
        server: ['SERVER'],
        disk: ['SSD', 'HDD'],
        memory: ['RAM'],
    }

    const warehouseTypeMapingCapacity = {
        server: 'CPU',
        disk: 'CAPACITY',
        memory: 'CAPACITY',
    }

    const handleModelChange = (value: any) => {
        if (!value) {
            formControl.setValue(
                `${name}.[${index}].capacity_value` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].brand_name` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].service_tag` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].capacity_unit` as Path<Contract>,
                null
            )

            return
        }

        const model = (models as Model[])?.find(
            (i) => i?.warehouse_model_name === value
        )

        const specification = model?.specifications?.find(
            (s) =>
                s.specification_name ===
                warehouseTypeMapingCapacity?.[
                    nameValue as keyof typeof warehouseTypeMapingCapacity
                ]
        )

        formControl.setValue(
            `${name}.[${index}].capacity_value` as Path<Contract>,
            parseFloat(specification?.specification_value || '0')
        )

        formControl.setValue(
            `${name}.[${index}].brand_name` as Path<Contract>,
            model?.manufacture?.manufacture_name
        )

        formControl.setValue(
            `${name}.[${index}].capacity_unit` as Path<Contract>,
            'core'
        )
    }

    return (
        <div
            className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200 mb-3"
            data-tour="config-server"
        >
            {/* Header */}
            <div className="px-3 py-2.5 bg-slate-50/50 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <ConfigurationIcons className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Typogaphy className="font-medium text-slate-800 text-sm truncate uppercase">
                                {t(
                                    formControl.getValues(
                                        `${name}.[${index}].name` as Path<Contract>
                                    )
                                )}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            </div>

            {/* Configuration Fields */}
            <div className="px-3 py-2.5">
                <GridHookForm
                    subForm
                    formControl={formControl}
                    className="grid-cols-3"
                    data={[
                        {
                            renderEditComponent: (control) => (
                                <div data-tour="server-model">
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].model_name` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('Model')}
                                        placeholder={t('Model')}
                                        size="small"
                                        onChangeAfterHookForm={(value) => {
                                            handleModelChange(value)
                                        }}
                                        options={(models as Model[])
                                            ?.filter((i) =>
                                                warehouseTypeMapingName?.[
                                                    nameValue as keyof typeof warehouseTypeMapingName
                                                ]?.includes(
                                                    i?.warehouse_type
                                                        ?.warehouse_type_name
                                                )
                                            )
                                            ?.map((i) => ({
                                                label: i?.warehouse_model_name,
                                                value: i?.warehouse_model_name,
                                            }))}
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div data-tour="server-manufacture">
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].brand_name` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('Manufacture')}
                                        placeholder={t('Manufacture')}
                                        size="small"
                                        disabled
                                        options={(
                                            manufactures as Manufacture[]
                                        )?.map((i) => ({
                                            label: i?.manufacture_name,
                                            value: i?.manufacture_name,
                                        }))}
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div data-tour="server-service-tag">
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].service_tag` as Path<Contract>
                                        }
                                        type="textfield"
                                        label={t('Service tag')}
                                        placeholder={t('Service tag')}
                                        size="small"
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div data-tour="server-vcores">
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].capacity_value` as Path<Contract>
                                        }
                                        type="number"
                                        label={t('vCores')}
                                        placeholder={t('vCores')}
                                        size="small"
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div data-tour="server-unit">
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].capacity_unit` as Path<Contract>
                                        }
                                        type="textfield"
                                        label={t('unit of calculation')}
                                        placeholder={t('unit of calculation')}
                                        size="small"
                                        disabled
                                    />
                                </div>
                            ),
                        },
                    ]}
                    actions={[]}
                />
            </div>
        </div>
    )
}

export default ServerWarehouse
